import { CaretDownIcon } from "@phosphor-icons/react/dist/csr/CaretDown";
import { useState, type ReactNode } from "react";
import { Button } from "@volca/ui/components/ui/button";
import { Stack } from "@volca/ui/components/ui/stack";
import { cn } from "@volca/ui/lib/utils";

/**
 * A "Label (N)" toggle over a group of rows — the Needs review / Resolved
 * groups in the invoice sidebar and on the review page. Renders nothing when
 * the group is empty.
 */
export const CollapsibleGroup = ({
  label,
  count,
  defaultOpen,
  children,
}: {
  label: string;
  count: number;
  defaultOpen: boolean;
  children: ReactNode;
}) => {
  // Follow the data until the user chooses a state. Keep that choice while
  // this group is empty, since polling can add actions again.
  const [openOverride, setOpenOverride] = useState<boolean | null>(null);
  const open = openOverride ?? defaultOpen;

  if (count === 0) return null;

  return (
    <Stack className="w-full" align="start" gap={1}>
      <Button
        variant="ghost"
        size="sm"
        aria-expanded={open}
        onClick={() => setOpenOverride(!open)}
        className="-ml-2.5 text-subtle"
      >
        {label} ({count})
        <CaretDownIcon
          data-icon="inline-end"
          className={cn("transition-transform", {
            "rotate-180": open,
          })}
        />
      </Button>
      {/* Keep drafts mounted while closed; hidden content cannot receive focus. */}
      <Stack
        align="start"
        gap={0}
        hidden={!open}
        className={cn("w-full", { hidden: !open })}
      >
        {children}
      </Stack>
    </Stack>
  );
};
